import { useForm } from '@tanstack/react-form';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { fieldErrorMessages } from '@/lib/forms/field-errors';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { notify } from '@/components/composed/notify';
import { ApiError } from '@/lib/api/errors';
import {
  providerFormSchema,
  toProviderInput,
  type ProviderFormValues,
} from '../schemas/provider-form';
import { useCreateProvider } from '../api/provider-mutations';

export function ProviderFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createProvider = useCreateProvider();

  const form = useForm({
    defaultValues: {
      name: '',
      dbaName: '',
      providerType: '',
      status: 'active',
    } as ProviderFormValues,
    validators: {
      onChange: providerFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await createProvider.mutateAsync(toProviderInput(value));
          return null;
        } catch (error) {
          if (error instanceof ApiError && error.fieldErrors?.length) {
            const fields: Record<string, string> = {};
            for (const fe of error.fieldErrors) fields[fe.field] = fe.message;
            return { fields };
          }
          return {
            form:
              error instanceof ApiError
                ? error.message
                : 'Failed to create provider.',
          };
        }
      },
    },
    onSubmit: () => {
      notify.success('Provider created.');
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create provider"
      onSubmit={() => void form.handleSubmit()}
      submitLabel="Create"
      pending={createProvider.isPending}
    >
      <form.Field name="name">
        {(field) => (
          <FormField
            label="Name"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="dbaName">
        {(field) => (
          <FormField label="DBA name">
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="providerType">
        {(field) => (
          <FormField label="Type">
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="status">
        {(field) => (
          <FormField label="Status">
            {({ id }) => (
              <Select
                value={field.state.value}
                onValueChange={(v) =>
                  field.handleChange(v as ProviderFormValues['status'])
                }
              >
                <SelectTrigger id={id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
      </form.Field>
    </FormDialog>
  );
}
